import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/DateInput";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AppShell } from "@/components/AppShell";
import { api, formatApiErrorDetail } from "@/api";
import { toast } from "sonner";
import { Download, FileSpreadsheet } from "lucide-react";
import { toDMY } from "@/lib/dates";

const L = ({ children }) => (
  <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--mod-primary)]">{children}</Label>
);

const today = () => new Date().toISOString().slice(0, 10);
const monthStart = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

export default function Reports() {
  const [params] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [type, setType] = useState(params.get("kind") || "both");
  const [groupBy, setGroupBy] = useState(params.get("group") || "bill");
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null);

  const load = async (group = groupBy) => {
    setLoading(true);
    try {
      if (group === "bill") {
        const { data } = await api.get("/reports/transactions", {
          params: { date_from: from, date_to: to, kind: type },
        });
        setData(data);
        setSummary(null);
      } else {
        const { data } = await api.get("/reports/summary", {
          params: { date_from: from, date_to: to, kind: type, group_by: group },
        });
        setSummary(data);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/profile").then(({ data }) => setProfile(data)).catch(() => {});
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const download = async (format) => {
    try {
      const summaryMode = groupBy !== "bill";
      const res = await api.get(summaryMode ? "/reports/summary/export" : "/reports/transactions/export", {
        params: { date_from: from, date_to: to, kind: type, format, ...(summaryMode ? { group_by: groupBy } : {}) },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const base = summaryMode ? `${type}-${groupBy}-wise-report` : "sales-purchase-report";
      a.download = `${base}.${format === "pdf" ? "pdf" : "xlsx"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Export failed");
    }
  };

  return (
    <AppShell profile={profile}>
      <main className="mx-auto max-w-[1500px] px-4 py-8 sm:px-8">
        <div className="mod-band rounded-sm px-5 py-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--mod-primary)]">Display</p>
          <h1 className="font-head mt-2 text-4xl font-extrabold tracking-tighter sm:text-5xl">
            Sales &amp; Purchase Report
          </h1>
        </div>

        <div className="grid grid-cols-1 gap-4 py-6 sm:grid-cols-2 lg:grid-cols-6 lg:items-end">
          <div>
            <L>From</L>
            <DateInput data-testid="report-from-input" className="mt-2 rounded-sm" inputClassName="h-9 w-full rounded-sm border border-zinc-200 bg-white px-3 text-sm outline-none focus:border-[var(--mod-primary)]" value={from} onChange={(v) => setFrom(v)} />
          </div>
          <div>
            <L>To</L>
            <DateInput data-testid="report-to-input" className="mt-2 rounded-sm" inputClassName="h-9 w-full rounded-sm border border-zinc-200 bg-white px-3 text-sm outline-none focus:border-[var(--mod-primary)]" value={to} onChange={(v) => setTo(v)} />
          </div>
          <div>
            <L>Type</L>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="report-type-select" className="mt-2 rounded-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="both">Sales + Purchase</SelectItem>
                <SelectItem value="sale">Sales only</SelectItem>
                <SelectItem value="purchase">Purchase only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <L>Group by</L>
            <Select
              value={groupBy}
              onValueChange={(v) => {
                setGroupBy(v);
                load(v);
              }}
            >
              <SelectTrigger data-testid="report-group-select" className="mt-2 rounded-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bill">Bill wise</SelectItem>
                <SelectItem value="party">Party wise</SelectItem>
                <SelectItem value="item">Item wise</SelectItem>
                <SelectItem value="company">Company wise</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button data-testid="report-run-button" className="rounded-sm bg-[var(--mod-primary)] text-white hover:opacity-90" onClick={() => load()}>
            Run report
          </Button>
          <div className="flex gap-2">
            <Button data-testid="report-pdf-button" variant="outline" className="rounded-sm" onClick={() => download("pdf")}>
              <Download size={15} className="mr-2" /> PDF
            </Button>
            <Button data-testid="report-excel-button" variant="outline" className="rounded-sm" onClick={() => download("excel")}>
              <FileSpreadsheet size={15} className="mr-2" /> Excel
            </Button>
          </div>
        </div>

        {loading ? (
          <p className="py-16 text-center text-sm text-zinc-500">Loading…</p>
        ) : groupBy !== "bill" ? (
          !summary || summary.rows.length === 0 ? (
            <div data-testid="report-empty-state" className="border border-dashed border-zinc-300 p-12 text-center">
              <h3 className="font-head text-xl font-bold tracking-tight">Nothing in this period</h3>
              <p className="mt-1 text-sm text-zinc-500">Pick another date range or record a sale or purchase first.</p>
            </div>
          ) : (
            <>
              <div className="mb-6 border border-zinc-200 bg-white p-4" data-testid="report-summary-totals">
                <div className="grid grid-cols-2 gap-x-4 gap-y-3 sm:flex sm:flex-wrap sm:gap-y-4">
                  {[
                    [groupBy === "party" ? "Parties" : groupBy === "item" ? "Items" : "Companies", summary.totals.groups],
                    ["Bills", summary.totals.bills],
                    ["Than", summary.totals.than],
                    ["Meter", summary.totals.meter],
                    ["Taxable", `₹${Number(summary.totals.taxable).toFixed(2)}`],
                    ["GST", `₹${Number(summary.totals.tax).toFixed(2)}`],
                    ["Total", `₹${Number(summary.totals.grand_total).toFixed(2)}`],
                  ].map(([label, value]) => (
                    <div key={label} className="min-w-0 border-zinc-200 sm:border-l sm:px-5 sm:first:border-l-0 sm:first:pl-0">
                      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">{label}</p>
                      <p className="font-mono-num truncate text-lg font-semibold sm:text-xl">{value}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="overflow-x-auto border border-zinc-200 bg-white" data-testid="report-summary-table">
                <table className="w-full text-sm">
                  <thead className="border-b border-zinc-200">
                    <tr className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400">
                      {["Type", groupBy === "party" ? "Party" : groupBy === "item" ? "Item" : "Company",
                        "Bills", "Lines", "Than", "Meter", "Taxable", "Discount", "GST", "Total"].map((h, i) => (
                        <th key={h} className={`px-3 py-2 ${i > 1 ? "text-right" : "text-left"}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                    {summary.rows.map((r, i) => (
                      <tr key={`${r.kind}-${r.label}`} data-testid={`report-summary-row-${i}`} className="hover:bg-zinc-50">
                        <td className="px-3 py-2 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">{r.kind}</td>
                        <td className="px-3 py-2 font-semibold uppercase">{r.label}</td>
                        <td className="font-mono-num px-3 py-2 text-right">{r.bills}</td>
                        <td className="font-mono-num px-3 py-2 text-right">{r.lines}</td>
                        <td className="font-mono-num px-3 py-2 text-right">{r.than}</td>
                        <td className="font-mono-num px-3 py-2 text-right">{r.meter}</td>
                        <td className="font-mono-num px-3 py-2 text-right">₹{Number(r.taxable).toFixed(2)}</td>
                        <td className="font-mono-num px-3 py-2 text-right">₹{Number(r.discount).toFixed(2)}</td>
                        <td className="font-mono-num px-3 py-2 text-right">₹{Number(r.tax).toFixed(2)}</td>
                        <td className="font-mono-num px-3 py-2 text-right font-semibold">₹{Number(r.grand_total).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )
        ) : !data || data.parties.length === 0 ? (
          <div data-testid="report-empty-state" className="border border-dashed border-zinc-300 p-12 text-center">
            <h3 className="font-head text-xl font-bold tracking-tight">Nothing in this period</h3>
            <p className="mt-1 text-sm text-zinc-500">Pick another date range or record a sale or purchase first.</p>
          </div>
        ) : (
          <>
            <div className="mb-6 border border-zinc-200 bg-white p-4" data-testid="report-totals">
            <div className="grid grid-cols-2 gap-x-4 gap-y-3 sm:flex sm:flex-wrap sm:gap-y-4">
              {[
                ["Entries", data.totals.entries],
                ["Than", data.totals.than],
                ["Meter", data.totals.meter],
                ["Taxable", `₹${Number(data.totals.amount).toFixed(2)}`],
                ["Discount", `₹${Number(data.totals.discount).toFixed(2)}`],
                ["GST", `₹${Number(data.totals.tax).toFixed(2)}`],
                ["Grand total", `₹${Number(data.totals.grand_total).toFixed(2)}`],
              ].map(([label, value]) => (
                <div key={label} className="min-w-0 border-zinc-200 sm:border-l sm:px-5 sm:first:border-l-0 sm:first:pl-0">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">{label}</p>
                  <p className="font-mono-num truncate text-lg font-semibold sm:text-xl">{value}</p>
                </div>
              ))}
            </div>
            </div>

            <div className="space-y-3">
              {data.parties.map((p) => (
                <div key={`${p.kind}-${p.party}`} data-testid={`report-party-${p.party || "unnamed"}`} className="border border-zinc-200 bg-white">
                  <button
                    className="flex w-full flex-wrap items-center justify-between gap-3 p-4 text-left"
                    onClick={() => setOpen(open === `${p.kind}-${p.party}` ? null : `${p.kind}-${p.party}`)}
                  >
                    <div>
                      <p className="font-head text-sm font-bold uppercase tracking-tight">{p.party || "(No party)"}</p>
                      <p className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">{p.kind}</p>
                    </div>
                    <p className="font-mono-num text-xs text-zinc-600">
                      {p.entries.length} entry(s) · {p.than} than · {p.meter} mtr · taxable ₹
                      {Number(p.amount).toFixed(2)} · GST ₹{Number(p.tax).toFixed(2)} · total ₹
                      {Number(p.grand_total).toFixed(2)}
                    </p>
                  </button>
                  {open === `${p.kind}-${p.party}` && (
                    <div className="divide-y divide-dashed divide-zinc-200 border-t border-zinc-200 px-4 pb-3">
                      {p.entries.map((e) => (
                        <div key={e.id} className="py-2">
                          <p className="font-mono-num text-xs text-zinc-700">
                            {toDMY(e.date)} · {e.ref_no} · {e.total_than} than · {e.total_meter} mtr · total ₹
                            {Number(e.grand_total ?? e.total_amount).toFixed(2)}
                          </p>
                          {e.items.map((it, i) => (
                            <p key={i} className="pl-3 text-xs text-zinc-500">
                              {it.quality_name}
                              {it.shade_number ? ` · shade ${it.shade_number}` : ""} — {it.than} than / {it.meter} mtr
                              {it.bale_no ? ` · Bale ${it.bale_no}` : ""}
                              {it.lr_no ? ` · LR ${it.lr_no}` : ""}
                              {it.rate ? ` @ ₹${it.rate}` : ""}
                            </p>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </main>
    </AppShell>
  );
}
